import { createRoute, redirect } from '@tanstack/react-router';
import { lazyPage } from '../common';
import { siteRoute } from '../layouts';

const pages = () => import('./pages');
const groupsComp = lazyPage(pages, 'CustomerGroupsHost');
const groupsBuilderComp = lazyPage(pages, 'CustomerGroupsBuilderHost');
const groupsQuickSetupComp = lazyPage(pages, 'CustomerGroupsQuickSetupHost');
const companiesOverviewComp = lazyPage(pages, 'CustomerCompaniesOverviewHost');
const companiesComp = lazyPage(pages, 'CompaniesHost');
const companyFormComp = lazyPage(pages, 'CompanyFormHost');
const sectionsComp = lazyPage(pages, 'SectionsHost');
const sectionFormComp = lazyPage(pages, 'SectionFormHost');
const customerDetailsComp = lazyPage(pages, 'CustomerDetailsHost');
const salesAgentsOverviewComp = lazyPage(pages, 'CustomerSalesAgentsOverviewHost');
const rolesComp = lazyPage(pages, 'RolesHost');
const roleFormComp = lazyPage(pages, 'RoleFormHost');

// --- v1 customers ---

// /customers redirects to the groups page.
const customersIndexRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/customers/groups', params });
  },
});

const customersGroupsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups',
  component: groupsComp,
  staticData: { title: 'Customer Groups' },
});

const customersGroupsCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups/create',
  component: groupsBuilderComp,
  staticData: { title: 'Create Customer Group' },
});

const customersGroupsEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'customers/groups/edit/$slug',
  component: groupsBuilderComp,
  staticData: { title: 'Edit Customer Group' },
});

// --- v2 customers ----

// /v2/customers redirects to the groups page.
const v2CustomersIndexRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/v2/customers/groups', params });
  },
});

const v2CustomersGroupsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/groups',
  component: groupsComp,
  staticData: { title: 'Customer Groups - Customer' },
});

const v2CustomersGroupsCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/groups/create',
  component: groupsBuilderComp,
  staticData: { title: 'Create Customer Group' },
});

const v2CustomersGroupsQuickSetupRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/groups/quick-setup',
  component: groupsQuickSetupComp,
  staticData: { title: 'Create Customer Group' },
});

const v2CustomersGroupsEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/groups/edit/$slug',
  component: groupsBuilderComp,
  staticData: { title: 'Edit Customer Group' },
});

const v2CustomerListRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/list',
  component: companiesOverviewComp,
  staticData: { title: 'Customers' },
});

const v2CustomersCompaniesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/companies',
  component: companiesComp,
  staticData: { title: 'Companies' },
});

const v2CustomersCompaniesCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/companies/create',
  component: companyFormComp,
  staticData: { title: 'Create Company' },
});

const v2CustomersCompaniesEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/companies/edit/$id',
  component: companyFormComp,
  staticData: { title: 'Edit Company' },
});

const v2CustomersCompaniesSectionsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/companies/$id/sections',
  component: sectionsComp,
  staticData: { title: 'Company Sections' },
});

const v2CustomersCompaniesSectionsCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/companies/$id/sections/create',
  component: sectionFormComp,
  staticData: { title: 'Create Section' },
});

const v2CustomersCompaniesSectionsEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/companies/$id/sections/edit/$sectionId',
  component: sectionFormComp,
  staticData: { title: 'Edit Section' },
});

const v2SalesAgentsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/agents',
  component: salesAgentsOverviewComp,
  staticData: { title: 'Sales Agents' },
});

const v2CustomersDetailsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/list/details/$id',
  component: customerDetailsComp,
  staticData: { title: 'Customer Details' },
});

const v2AgentsDetailsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/agents/details/$id',
  component: customerDetailsComp,
  staticData: { title: 'Sales Agents Details' },
});

const v2CustomersRolesRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/roles',
  component: rolesComp,
  staticData: { title: 'Roles' },
});

const v2CustomersRolesCreateRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/roles/create',
  component: roleFormComp,
  staticData: { title: 'Create Role' },
});

const v2CustomersRolesEditRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'v2/customers/roles/edit/$slug',
  component: roleFormComp,
  staticData: { title: 'Edit Role' },
});

/** Customers family leaves (v1 + v2) to attach under the site-env layout. */
export const customersLeaves = [
  customersIndexRoute,
  customersGroupsRoute,
  customersGroupsCreateRoute,
  customersGroupsEditRoute,
  v2CustomersIndexRoute,
  v2CustomersGroupsRoute,
  v2CustomersGroupsCreateRoute,
  v2CustomersGroupsQuickSetupRoute,
  v2CustomersGroupsEditRoute,
  v2CustomerListRoute,
  v2CustomersCompaniesRoute,
  v2CustomersCompaniesCreateRoute,
  v2CustomersCompaniesEditRoute,
  v2CustomersCompaniesSectionsRoute,
  v2CustomersCompaniesSectionsCreateRoute,
  v2CustomersCompaniesSectionsEditRoute,
  v2SalesAgentsRoute,
  v2CustomersDetailsRoute,
  v2AgentsDetailsRoute,
  v2CustomersRolesRoute,
  v2CustomersRolesCreateRoute,
  v2CustomersRolesEditRoute,
];
